import { ADULTS_KEY } from '@yoyoclub/shared';
import {
  Activity as BodyIcon,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Heart,
  LayoutGrid,
  Lightbulb,
  MessageCircle,
  Plus,
  SlidersHorizontal,
  User,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { listSeparator } from '../../i18n/script';
import PickCard, { type PickCardData } from '../picks/PickCard';
import PicksMark from '../picks/PicksMark';
import { PicksNote } from '../picks/PicksNote';
import ActivityCard from '../ui/ActivityCard';
import { btnPrimarySm, focusRing, textLink } from '../ui/redesign';

import { useFilterSummary } from './filterText';
import {
  activitiesLink,
  AUDIENCE_THEME,
  isFiltered,
  picksLink,
  type HomeSelection,
} from './homeState';

type CardProps = Parameters<typeof ActivityCard>[0];

export interface RecommendationView {
  /** Changes with every response; the rails remount so they start at the first card. */
  key: string;
  activities: CardProps[];
  activitiesTotal: number;
  inPersonTotal: number;
  picks: PickCardData[];
  stageActivityCount: number | null;
}

const DOMAINS = [
  { key: 'body', Icon: BodyIcon },
  { key: 'language', Icon: MessageCircle },
  { key: 'social', Icon: Heart },
  { key: 'cognitive', Icon: Lightbulb },
] as const;

const SIDE = 'px-4 desk:px-8';

/** Row title, "查看全部", desktop arrows, then the swipeable rail (prototype `.row`). */
function Row({
  title,
  link,
  label,
  note,
  children,
}: {
  title: ReactNode;
  link?: ReactNode;
  label: string;
  note?: ReactNode;
  children?: ReactNode;
}) {
  const { t } = useTranslation('home');
  const ref = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ start: true, end: true });
  const hasRail = !!children;

  const sync = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setEdges({
      start: el.scrollLeft <= 2,
      end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 2,
    });
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, [sync, hasRail]);

  const scroll = (dir: 1 | -1) =>
    ref.current?.scrollBy({ left: dir * ref.current.clientWidth * 0.85, behavior: 'smooth' });

  return (
    <div className="order-2 mt-5 desk:mt-[26px]">
      <div className={`mb-2.5 flex items-center gap-2.5 ${SIDE}`}>
        <h3 className="flex items-center gap-2 font-ui text-[18px] font-semibold leading-[1.6] text-ink">
          {title}
        </h3>
        <div className="flex-1" />
        {link}
        {hasRail && (
          <div className="hidden gap-1.5 desk:flex">
            {([-1, 1] as const).map((dir) => (
              <button
                key={dir}
                type="button"
                disabled={dir === -1 ? edges.start : edges.end}
                onClick={() => scroll(dir)}
                aria-label={dir === -1 ? t('block.scrollLeft') : t('block.scrollRight')}
                className={`grid h-9 w-9 place-items-center rounded-full border-[1.5px] border-line bg-white text-ink disabled:cursor-default disabled:opacity-35 ${focusRing}`}
              >
                {dir === -1 ? (
                  <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <ChevronRight className="h-4 w-4" aria-hidden="true" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
      {note}
      {hasRail && (
        <div
          ref={ref}
          onScroll={sync}
          role="region"
          aria-label={label}
          className="flex snap-x snap-mandatory items-stretch gap-3 overflow-x-auto scroll-smooth scroll-px-4 px-4 pb-2.5 pt-1 [scrollbar-width:none] desk:gap-4 desk:scroll-px-8 desk:px-8 [&::-webkit-scrollbar]:hidden [&>*]:flex-none [&>*]:snap-start"
        >
          {children}
        </div>
      )}
    </div>
  );
}

function EndTile({
  to,
  icon,
  label,
  hint,
}: {
  to: string;
  icon: ReactNode;
  label: string;
  hint: string;
}) {
  return (
    <Link
      to={to}
      className={`flex w-[42vw] flex-col items-center justify-center gap-2.5 rounded-[18px] border-[1.5px] border-dashed border-line bg-white/55 p-4 text-center font-ui text-[14.5px] font-semibold text-primary-dark desk:w-[180px] ${focusRing}`}
    >
      <span className="grid h-11 w-11 place-items-center rounded-full bg-white text-primary">
        {icon}
      </span>
      {label}
      <small className="font-text text-[12.5px] font-normal text-mute">{hint}</small>
    </Link>
  );
}

const notice =
  'mx-4 mb-1 mt-0.5 rounded-[14px] border border-dashed border-pick-line bg-white/70 px-3.5 py-2.5 text-sm text-ink-2 desk:mx-8';

/**
 * The recommendation block (PRD §3.1 #3, §3.3, §3.6): sticky context bar on phones,
 * heading, the activity row above the Picks row (S8), and the development cards
 * (before the rows on desktop, after them on phones).
 */
export default function RecommendationBlock({
  sel,
  onChange,
  view,
  failed,
  onOpenStageSheet,
  onOpenFilterSheet,
}: {
  sel: HomeSelection;
  onChange: (next: HomeSelection) => void;
  view: RecommendationView | null;
  /** The first load failed; nothing to show yet. */
  failed: boolean;
  onOpenStageSheet: () => void;
  onOpenFilterSheet: () => void;
}) {
  const { t, i18n } = useTranslation(['home', 'common', 'picks']);
  const summary = useFilterSummary();
  const isParents = sel.audience === ADULTS_KEY;
  const theme = AUDIENCE_THEME[sel.audience];
  const filtered = isFiltered(sel);
  const stageName = isParents ? '' : t(`home:stages.${sel.audience}.name`);
  const clearFilters = () => onChange({ ...sel, when: 'anytime', regions: [] });
  const sum = summary(sel);

  const activities = view?.activities ?? [];
  const picks = view?.picks ?? [];
  const stageCount = view?.stageActivityCount ?? null;
  // S8: the stage has activities, only time or place hid them.
  const noneByFilter =
    !!view && filtered && view.inPersonTotal === 0 && (isParents || (stageCount ?? 0) > 0);
  const scarce = !!view && !isParents && stageCount !== null && stageCount < 3;
  const bothEmpty = !!view && activities.length === 0 && picks.length === 0;

  const actf = (label: string, clearLabel: string, clear: () => void) => (
    <span className="inline-flex items-center gap-1 rounded-full bg-white py-1 pl-[11px] pr-1.5 font-ui text-[13px] text-ink-2">
      {label}
      <button
        type="button"
        aria-label={clearLabel}
        onClick={clear}
        className={`grid min-h-[24px] min-w-[24px] place-items-center rounded-full p-0.5 text-mute ${focusRing}`}
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </span>
  );

  return (
    <section id="stage" className="mx-auto max-w-[1160px] px-4 desk:px-7">
      <div className="-mx-2 flex flex-col rounded-[24px] bg-[var(--ac-tint)] pb-5 pt-2.5 desk:mx-0 desk:mt-1 desk:rounded-[32px] desk:pb-[26px] desk:pt-[30px]">
        {/* Phone: sticky bar with the current stage and the time/place summary */}
        <div className="sticky top-[calc(env(safe-area-inset-top,0px)+68px)] z-30 order-[-1] mx-2 mb-3.5 flex gap-1.5 rounded-[18px] border border-line bg-white p-[5px] shadow-[0_12px_28px_-18px_rgba(60,50,30,0.6)] desk:hidden">
          <button
            type="button"
            onClick={onOpenStageSheet}
            aria-label={t('home:filters.switchStage')}
            className={`flex min-h-[44px] min-w-0 flex-1 items-center gap-2 rounded-[13px] bg-[var(--ac-tint)] px-2.5 py-2 text-left font-ui text-ink ${focusRing}`}
          >
            <span className="h-2.5 w-2.5 flex-none rounded-[3px] bg-[var(--ac)]" />
            <b className="whitespace-nowrap text-[15px] font-bold">
              {isParents
                ? t('home:selector.parents')
                : `${theme.age} ${t('home:selector.yearsUnit')}`}
            </b>
            <span className="min-w-0 truncate text-[12.5px] text-ink-2">
              {isParents ? t('home:selector.parentsSub') : stageName}
            </span>
            <ChevronDown className="ml-auto h-4 w-4 flex-none text-mute" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={onOpenFilterSheet}
            aria-label={t('home:filters.sheetTitle')}
            className={`flex min-h-[44px] max-w-[48%] items-center gap-1.5 whitespace-nowrap rounded-[13px] px-3 py-2 font-ui text-[13.5px] font-semibold ${
              sum ? 'bg-primary-pale text-primary-dark' : 'bg-warm-white text-ink'
            } ${focusRing}`}
          >
            <SlidersHorizontal className="h-4 w-4 flex-none text-primary" aria-hidden="true" />
            <span className="truncate">{sum || t('home:filters.summaryEmpty')}</span>
          </button>
        </div>

        <div className={`order-0 ${SIDE}`}>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2.5">
            <span className="hidden items-center gap-1.5 rounded-full bg-[var(--ac)] px-[13px] py-[5px] font-ui text-[13px] font-semibold text-[var(--ac-on)] desk:inline-flex">
              {isParents ? (
                <>
                  <User className="h-[18px] w-[18px]" aria-hidden="true" />
                  {t('home:selector.parents')}
                </>
              ) : (
                t('home:block.stageChip', { age: theme.age, name: stageName })
              )}
            </span>
            <h2 className="font-ui text-[20px] font-bold leading-[1.6] text-ink desk:text-[clamp(22px,2.4vw,30px)] [text-wrap:balance]">
              {isParents
                ? t('home:block.parentsTitle')
                : t('home:block.stageTitle', { age: theme.age })}
            </h2>
            {sel.when !== 'anytime' &&
              actf(t(`home:filters.times.${sel.when}`), t('home:filters.clearTime'), () =>
                onChange({ ...sel, when: 'anytime' })
              )}
            {sel.regions.length > 0 &&
              actf(
                sel.regions.map((r) => t(`common:region.${r}`)).join(listSeparator(i18n.language)),
                t('home:filters.clearPlace'),
                () => onChange({ ...sel, regions: [] })
              )}
          </div>
          <p className="mb-0 mt-1.5 max-w-[46em] text-[14.5px] text-ink-2 desk:mb-[18px] desk:mt-2 desk:text-[15.5px]">
            {isParents ? t('home:block.parentsIntro') : t(`home:stages.${sel.audience}.intro`)}
          </p>
        </div>

        {failed ? (
          <div className="order-2 mx-4 mt-5 rounded-2xl bg-white p-[26px] text-center text-ink-2 desk:mx-8 desk:mt-[26px]">
            {t('home:block.loadFailed')}
          </div>
        ) : !view ? (
          // Rows mount with their data: a rail that gains cards after mounting keeps
          // its scroll snapped to the old card instead of starting at the first one.
          <div className="order-2 min-h-[420px]" aria-busy="true" />
        ) : bothEmpty ? (
          <div className="order-2 mx-4 mt-5 rounded-2xl bg-white p-[26px] text-center text-ink-2 desk:mx-8 desk:mt-[26px]">
            {t('home:block.empty')}
            <br />
            {filtered ? (
              <button type="button" className={`${btnPrimarySm} mt-3`} onClick={clearFilters}>
                {t('home:block.clearFilters')}
              </button>
            ) : (
              <Link to="/activities" className={`${btnPrimarySm} mt-3`}>
                {t('home:block.browseAll')}
              </Link>
            )}
          </div>
        ) : (
          <>
            <Row
              key={`a-${view.key}`}
              label={
                isParents ? t('home:block.parentsActivitiesRow') : t('home:block.activitiesRow')
              }
              title={
                <>
                  <span className="h-2.5 w-2.5 rounded-[3px] bg-primary" />
                  {isParents ? t('home:block.parentsActivitiesRow') : t('home:block.activitiesRow')}
                </>
              }
              link={
                activities.length > 0 ? (
                  <Link to={activitiesLink(sel)} className={textLink}>
                    {t('home:block.viewAll')}
                  </Link>
                ) : undefined
              }
              note={
                <>
                  {noneByFilter && (
                    <div className={notice}>
                      {t('home:block.noneByFilter')}
                      <button type="button" className={`${textLink} ml-1.5`} onClick={clearFilters}>
                        {t('home:block.clearFilters')}
                      </button>
                    </div>
                  )}
                </>
              }
            >
              {activities.length > 0 ? (
                <>
                  {activities.map((card) => (
                    <div key={`${card.itemType}-${card.id}`} className="w-[78vw] desk:w-[288px]">
                      <ActivityCard {...card} />
                    </div>
                  ))}
                  <EndTile
                    to={activitiesLink(sel)}
                    icon={<LayoutGrid className="h-[18px] w-[18px]" aria-hidden="true" />}
                    label={t('home:block.viewAllCount', { count: view?.activitiesTotal ?? 0 })}
                    hint={t('home:block.viewAllHint')}
                  />
                </>
              ) : null}
            </Row>
            {!noneByFilter && scarce && (
              <div className={`order-2 mt-0.5 ${notice}`}>{t('home:block.scarce')}</div>
            )}
            <Row
              key={`p-${view.key}`}
              label={t('picks:name')}
              title={<PicksMark />}
              link={
                <Link to={picksLink(sel)} className={textLink}>
                  {t('home:block.viewAll')}
                </Link>
              }
              note={<PicksNote className={`-mt-0.5 mb-3 ${SIDE}`} />}
            >
              {picks.map((pick) => (
                <PickCard key={pick.id} pick={pick} className="w-[66vw] desk:w-[236px]" />
              ))}
              <EndTile
                to="/picks/submit"
                icon={<Plus className="h-[18px] w-[18px]" aria-hidden="true" />}
                label={t('home:block.suggest')}
                hint={t('home:block.suggestHint')}
              />
            </Row>
          </>
        )}

        {!isParents && (
          <div className={`order-3 desk:order-1 ${SIDE}`}>
            <h3 className="mb-2.5 mt-6 font-ui text-[15px] font-semibold leading-[1.6] text-ink desk:hidden">
              {t('home:block.developing', { age: theme.age })}
            </h3>
            <div className="grid grid-cols-2 gap-2 desk:grid-cols-4 desk:gap-3">
              {DOMAINS.map(({ key, Icon }) => (
                <div
                  key={key}
                  className="flex items-center gap-[9px] rounded-[14px] bg-white p-2.5 desk:items-start desk:gap-[11px] desk:rounded-2xl desk:px-3.5 desk:py-[13px]"
                >
                  <span className="grid h-[30px] w-[30px] flex-none place-items-center rounded-[9px] bg-[var(--ac-tint)] text-[var(--ac-dk)] desk:h-[34px] desk:w-[34px] desk:rounded-[11px]">
                    <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
                  </span>
                  <div>
                    <div className="font-ui text-[11px] text-mute desk:text-[11.5px]">
                      {t(`home:domains.${key}`)}
                    </div>
                    <div className="text-[13.5px] font-bold leading-[1.3] desk:text-[14.5px]">
                      {t(`home:stages.${sel.audience}.${key}.title`)}
                    </div>
                    <div className="mt-0.5 hidden text-[13px] leading-[1.5] text-ink-2 desk:block">
                      {t(`home:stages.${sel.audience}.${key}.desc`)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
